import {
  type Coordinate,
  type Track,
  type TrackSessionDto,
  type RawTelemetryData,
  type Device,
  type SessionFeatures,
  processTrackSessionWithTrackDto,
  processRawTelemetryWithTrackDto,
  isEmptyGuid,
} from '@pitbrain/shared'
import useApi from '../../api/useApi'
import ConfigurationContext from '../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { useTrackSessionContext } from '../trackSession/TrackSessionContext'

/**
 * Check if a new track can be added to an existing session.
 * Throws an error if the tracks are incompatible (different known tracks, or mixing known/unknown).
 */
const validateTrackCompatibility = (
  existingTrackId: string | undefined,
  newTrackId: string | undefined
): void => {
  const existingIsUnknown = isEmptyGuid(existingTrackId)
  const newIsUnknown = !newTrackId

  const tracksDiffer =
    existingIsUnknown !== newIsUnknown ||
    (!existingIsUnknown && !newIsUnknown && existingTrackId !== newTrackId)

  if (tracksDiffer) {
    throw Error(
      'The uploaded file is from a different track than the session that is already loaded.'
    )
  }
}

export const useBaseProcessor = () => {
  const { setTrackSessions, trackSessions } = useTrackSessionContext()

  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const loadTrack = async (
    trackSession: TrackSessionDto,
    _sessionFeatures: SessionFeatures
  ): Promise<void> => {
    const track = await api.trackApi.getTrack(trackSession.trackId)
    if (track == null) {
      return
    }

    const sharedLapIds = await api.trackSessionApi.getSharedLapsForTrackSession(
      trackSession.id
    )

    const result = await processTrackSessionWithTrackDto(
      trackSession,
      track,
      []
    )
    const ts = result.trackSession

    // Store shared lap IDs on the session - will be used by applyStartFinishForSession
    // to mark laps as shared when they're created
    ts.sharedLapIds = new Set(sharedLapIds?.map((l) => l.id) ?? [])

    if (
      trackSessions?.length &&
      trackSessions[0].track?.id !== trackSession.trackId
    ) {
      throw Error(
        'The uploaded file is from a different track than the session that is already loaded.'
      )
    }
    setTrackSessions((prevState) => [...prevState, ts])
  }

  const getFirstCoordinate = (
    rawTelemetry: RawTelemetryData
  ): Coordinate | null => {
    if (!rawTelemetry.gps || rawTelemetry.gps.length === 0) {
      return null
    }
    const firstPoint = rawTelemetry.gps[0]
    return {
      latitude: firstPoint.latitude,
      longitude: firstPoint.longitude,
    }
  }

  const processAndCreateSession = async (
    rawTelemetry: RawTelemetryData,
    sessionId: string,
    track: Track | null,
    sessionFeatures: SessionFeatures,
    fileName: string,
    device: Device
  ): Promise<void> => {
    if (trackSessions?.length) {
      validateTrackCompatibility(trackSessions[0].track?.id, track?.id)
    }

    const result = await processRawTelemetryWithTrackDto(
      rawTelemetry,
      track, // Will use unknown track if null
      {
        sessionId,
        fileName,
        device,
        sessionFeatures,
      }
    )

    setTrackSessions((prevState) => [...prevState, result.trackSession])
  }

  return { loadTrack, getFirstCoordinate, processAndCreateSession }
}
